"use client";

import FlightCard from "@/containers/website/flight/results/FlightResultsBody/mainContent/components/flightCard";
import type { Airline } from "@/shared/components/common/AirlinesSlider";
import Typography from "@/shared/components/common/Typography";
import type { Flight } from "@/types/services/flight/apis/Availability";
import { useState } from "react";

export interface CheapestFlight {
  id: string;
  airline: string;
  airlineCode?: string;
  airports?: Flight["airports"];
  arrivalTime: string;
  baggageAllowance?: string;
  cabinClass: string;
  cabinType?: string;
  capacity: number;
  departure: string;
  departureTime: string;
  destination: string;
  destinationIata: string;
  destinationId: number;
  flightKey?: string;
  flightNumber: string;
  origin: string;
  originIata: string;
  originId: number;
  price: number;
  priceDetails?: {
    adult?: { payable?: string | number };
    child?: { payable?: string | number };
    infant?: { payable?: string | number };
  };
  type: string;
  planeName?: string;
}

export interface CheapestFlightGroup {
  id: number;
  city: string;
  flights: CheapestFlight[];
}

interface CheapestFlightsTodayProps {
  groups: CheapestFlightGroup[];
  airlines: Airline[];
}

const cabinTypes = new Set<Flight["cabin_type"]>([
  "ECONOMY",
  "BUSINESS",
  "PREMIUM",
  "PREMIUMECONOMY",
  "FIRST",
  "BUSINESSPREMIUM",
]);

function amountInTomans(value: string | number | undefined, fallback: number) {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount / 10) : fallback;
}

function CachedFlightCard({
  flight,
  airlines,
}: {
  flight: CheapestFlight;
  airlines: Airline[];
}) {
  const airline = airlines.find(
    ({ code }) => code.toUpperCase() === flight.airlineCode?.toUpperCase(),
  );
  const price = Math.round(flight.price / 10);
  const cabinType = cabinTypes.has(flight.cabinType as Flight["cabin_type"])
    ? (flight.cabinType as Flight["cabin_type"])
    : "ECONOMY";
  const mappedFlight: Flight = {
    status: "available",
    type: flight.type === "چارتری" ? "CHARTER" : "SYSTEM",
    type_lang: flight.type,
    cabin_type: cabinType,
    cabin_type_lang: flight.cabinClass,
    departure_time: flight.departureTime,
    arrival_time: flight.arrivalTime,
    airline_logo: airline?.logo ?? "/favicon.ico",
    airline_name: airline?.name ?? flight.airline,
    airline_name_lang: flight.airline,
    arrival_city_name: flight.destination,
    arrival_city_name_lang: flight.destination,
    departure_city_name: flight.origin,
    departure_city_name_lang: flight.origin,
    duration: "",
    flight_no: flight.flightNumber,
    plane_name: flight.planeName ?? "",
    date: flight.departure,
    airline_code: flight.airlineCode,
    flight_key: flight.flightKey ?? flight.id,
    price,
    capacity: String(flight.capacity),
    price_details: {
      adult: {
        payable: amountInTomans(flight.priceDetails?.adult?.payable, price),
      },
      child: {
        payable: amountInTomans(flight.priceDetails?.child?.payable, price),
      },
      infant: {
        payable: amountInTomans(flight.priceDetails?.infant?.payable, price),
      },
    },
    tags: [],
    airports: flight.airports,
    baggage: flight.baggageAllowance
      ? { adult: flight.baggageAllowance }
      : null,
  };
  // The shared legacy type contains unrelated hotel and campaign fields. The
  // card only reads the flight fields supplied here.
  const searchParams = {
    depart: flight.departure,
    adult: "1",
    child: "0",
    infant: "0",
    origin: String(flight.originId),
    destination: String(flight.destinationId),
    flightType: "one-way",
  } as SearchParams;

  return (
    <FlightCard
      flight={mappedFlight}
      searchParams={searchParams}
      basePath={`/flights/${flight.originIata}-${flight.destinationIata}/`}
    />
  );
}

export default function CheapestFlightsToday({
  groups,
  airlines,
}: CheapestFlightsTodayProps) {
  const [selectedCityId, setSelectedCityId] = useState(groups[0]?.id);
  const selectedGroup =
    groups.find((group) => group.id === selectedCityId) ?? groups[0];

  if (!selectedGroup) return null;

  return (
    <section className="mb-8 rounded-xl border border-border bg-muted/30 px-4 py-6 md:mb-12 md:px-6 md:py-8">
      <div className="mb-5">
        <Typography variant="h2" className="text-xl font-bold md:text-2xl">
          ارزان‌ترین پروازهای امروز
        </Typography>
        <Typography className="mt-1 text-sm text-muted-foreground">
          کمترین قیمت‌های موجود برای هر شهر
        </Typography>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {groups.map((group) => {
          const active = group.id === selectedGroup.id;
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => setSelectedCityId(group.id)}
              className={`rounded-full border px-4 py-2 text-sm transition ${
                active
                  ? "border-primary bg-primary font-semibold text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              از {group.city}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {selectedGroup.flights.map((flight) => (
          <CachedFlightCard key={flight.id} flight={flight} airlines={airlines} />
        ))}
      </div>
    </section>
  );
}
